import { AntiBotClientStrategy, AntiBotPublicConfig } from "./types";

const SCRIPT_ID = "recaptcha-v3";
const GRECAPTCHA_WAIT_TIMEOUT_MS = 5000;
const GRECAPTCHA_POLL_INTERVAL_MS = 100;

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

const getSiteKey = (publicConfig: AntiBotPublicConfig): string | null =>
  typeof publicConfig?.siteKey === "string" ? publicConfig.siteKey : null;

const waitForGrecaptcha = (): Promise<Grecaptcha | null> => {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (window.grecaptcha?.execute) {
    return Promise.resolve(window.grecaptcha);
  }

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const interval = setInterval(() => {
      if (window.grecaptcha?.execute) {
        clearInterval(interval);
        resolve(window.grecaptcha);
        return;
      }

      if (Date.now() - startedAt >= GRECAPTCHA_WAIT_TIMEOUT_MS) {
        clearInterval(interval);
        resolve(null);
      }
    }, GRECAPTCHA_POLL_INTERVAL_MS);
  });
};

export const recaptchaV3Strategy: AntiBotClientStrategy = {
  attribution: "Este site é protegido pelo reCAPTCHA",

  ensureScript(publicConfig) {
    const siteKey = getSiteKey(publicConfig);
    if (!siteKey || typeof document === "undefined") {
      return;
    }

    if (document.getElementById(SCRIPT_ID)) {
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    document.head.appendChild(script);
  },

  async execute(action, publicConfig) {
    const siteKey = getSiteKey(publicConfig);
    if (!siteKey) {
      return null;
    }

    const grecaptcha = await waitForGrecaptcha();
    if (!grecaptcha) {
      return null;
    }

    try {
      await new Promise<void>((resolve) => grecaptcha.ready(() => resolve()));
      return await grecaptcha.execute(siteKey, { action });
    } catch {
      return null;
    }
  },
};
